import React, { useEffect, useMemo, useState } from 'react'
import { BlockComponentFactory } from '../BlockFactory'
import type { ListBlockProps } from './block-props.types'
import { useBlockEngine } from '../BlockEngineContext'
import { ITaskBlock } from '../ITaskBlock'
import { ListBlock } from './ListBlock'
import { BlockConfigWithChildren, PropertyBinding } from '../../core/types'
import { JSONPath } from 'jsonpath-plus'
import { updateLayoutBlockChildIds } from './layoutBlockHandlers'
import { Logger } from '../../core/Logger'
import { toCSSSize } from './block-utils'

type ListBlockRuntimeProps = Omit<ListBlockProps, 'value'> & {
    childBlockConfigs: BlockConfigWithChildren[]
    value?: ListBlockProps['value'] | string
}

export const ListBlockRuntimeComponent =
    BlockComponentFactory.createBlockComponent<ListBlockRuntimeProps>(props => {
        const { value = [], childBlockConfigs = [], gap = 8, ...restProps } = props
        const { blockEngine } = useBlockEngine()
        const listBlockId = props.blockId
        const [itemBlocks, setItemBlocks] = useState<ITaskBlock[][]>([])
        const createdBlockIdsRef = React.useRef<Set<string>>(new Set())

        const data: ListBlockProps['value'] = useMemo(() => {
            try {
                if (typeof value === 'string') {
                    const parsed = JSON.parse(value)
                    if (!Array.isArray(parsed)) {
                        const logger = Logger.getInstance()
                        logger.warn(
                            `List block expected an array value but received ${typeof parsed} after parsing. Falling back to empty list.`,
                            {
                                source: 'ListBlock',
                                context: { blockId: listBlockId },
                            }
                        )
                        return []
                    }
                    return parsed
                }
                if (value !== null && value !== undefined && !Array.isArray(value)) {
                    const logger = Logger.getInstance()
                    logger.warn(
                        `List block expected an array value but received "${typeof value}". Falling back to empty list.`,
                        {
                            source: 'ListBlock',
                            context: { blockId: listBlockId, receivedType: typeof value },
                        }
                    )
                    return []
                }
                return value
            } catch (error) {
                const logger = Logger.getInstance()
                logger.error('List block failed to parse value', {
                    source: 'ListBlock',
                    error: error instanceof Error ? error : String(error),
                    context: {
                        blockId: listBlockId,
                        hint: 'Ensure the list value is a valid JSON array.',
                    },
                })
                return []
            }
        }, [value, listBlockId])

        useEffect(() => {
            if (!blockEngine || childBlockConfigs.length === 0) {
                return
            }

            const newItemBlocks: ITaskBlock[][] = []
            const usedBlockIds = new Set<string>()

            const createChildBlocks = (
                blockConfig: BlockConfigWithChildren,
                itemData: Record<string, unknown>,
                prefix: string,
                itemIndex: number
            ) => {
                const childConfigs: BlockConfigWithChildren[] = blockConfig.children || []
                const childBlocks: ITaskBlock[] = []
                childConfigs.forEach(childConfig => {
                    const childBlock = createBlockRecursively(
                        childConfig,
                        itemData,
                        prefix,
                        itemIndex
                    )
                    if (childBlock) {
                        childBlocks.push(childBlock)
                    }
                })
                return childBlocks
            }

            const createBlockRecursively = (
                config: BlockConfigWithChildren,
                itemData: Record<string, unknown>,
                prefix: string,
                itemIndex: number
            ): ITaskBlock | null => {
                const templateBlock = blockEngine.getHiddenBlock(config.id)
                if (!templateBlock) {
                    console.error(`Template block ${config.id} not found in hiddenBlocks`)
                    return null
                }

                const listeners = templateBlock.getListeners()

                try {
                    const blockId = `${prefix}-${config.id}`
                    usedBlockIds.add(blockId)

                    const props = { ...config.properties, id: blockId } as Record<string, unknown>

                    const bindings = config.bindings
                    if (bindings) {
                        Object.entries(bindings as Record<string, PropertyBinding>).forEach(
                            ([bindingKey, bindingValue]) => {
                                if (bindingValue.type === 'dataBinding') {
                                    const stateValue = JSONPath({
                                        path: bindingValue.value,
                                        json: itemData,
                                        wrap: false,
                                    })
                                    props[bindingKey] = stateValue
                                }
                            }
                        )
                    }

                    if (config.children) {
                        const childBlocks = createChildBlocks(config, itemData, prefix, itemIndex)
                        props['childBlockConfigIds'] = childBlocks.map(block => block.id)

                        const idMapping = new Map<string, string>()
                        config.children.forEach((childConfig, index) => {
                            if (childBlocks[index]) {
                                idMapping.set(childConfig.id, childBlocks[index].id)
                            }
                        })

                        updateLayoutBlockChildIds(config.type, props, idMapping)
                    }

                    const existingBlock = blockEngine.getBlock(blockId)

                    if (existingBlock) {
                        if (existingBlock.updateProps) {
                            existingBlock.updateProps(props)
                        } else {
                            blockEngine.updateBlock(blockId, props)
                        }
                        return existingBlock
                    } else {
                        const block = blockEngine.addBlock(config.type, {
                            id: blockId,
                            name: config.name,
                            componentProps: props,
                        })

                        if (listeners) {
                            Object.entries(listeners).forEach(([listenerKey, listenerSignal]) => {
                                const listener = listenerSignal.value
                                if (listener) {
                                    block?.setListener(listenerKey, (...args: unknown[]) => {
                                        listener(args[0], blockId, config.name, itemIndex)
                                    })
                                }
                            })
                        }
                        createdBlockIdsRef.current.add(blockId)
                        return block
                    }
                } catch (error) {
                    console.error(
                        `Error creating/updating block ${config.id} for list item ${itemIndex}:`,
                        error
                    )
                    return null
                }
            }

            data?.forEach((item: Record<string, unknown>, itemIndex: number) => {
                const blocksForThisItem: ITaskBlock[] = []
                const prefix = `${listBlockId}-${itemIndex}`

                childBlockConfigs.forEach(childBlockConfig => {
                    const block = createBlockRecursively(childBlockConfig, item, prefix, itemIndex)
                    if (block) {
                        blocksForThisItem.push(block)
                    }
                })

                newItemBlocks.push(blocksForThisItem)
            })

            createdBlockIdsRef.current.forEach(blockId => {
                if (!usedBlockIds.has(blockId)) {
                    blockEngine.removeBlock(blockId)
                }
            })
            createdBlockIdsRef.current = usedBlockIds
            setItemBlocks(newItemBlocks)
        }, [blockEngine, listBlockId, childBlockConfigs, data])

        useEffect(() => {
            const refSet = createdBlockIdsRef.current
            return () => {
                Array.from(refSet).forEach(blockId => {
                    if (blockEngine.getBlock(blockId)) {
                        blockEngine.removeBlock(blockId)
                    }
                })
                refSet.clear()
            }
        }, [blockEngine])

        const { emptyMessage = 'No items', direction = 'vertical', itemWidth = 'auto' } = restProps
        const isHorizontal = direction === 'horizontal'

        const renderItemContent = (index: number) => {
            const blocksForItem = itemBlocks[index] || []
            return (
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: `${gap}px`,
                        width: '100%',
                    }}
                >
                    {blocksForItem.map(b => (
                        <React.Fragment key={b.id}>{b.getElement()?.element}</React.Fragment>
                    ))}
                </div>
            )
        }

        const emptyPlaceholder = (
            <div
                style={{
                    flex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#999',
                    padding: '20px',
                }}
            >
                {emptyMessage}
            </div>
        )

        const listContent =
            data && data.length > 0 ? (
                <div
                    style={{
                        display: 'flex',
                        flexDirection: isHorizontal ? 'row' : 'column',
                        gap: `${gap}px`,
                    }}
                >
                    {data.map((_, index) => (
                        <div
                            key={index}
                            style={{
                                width: toCSSSize(itemWidth),
                                flexShrink: 0,
                            }}
                        >
                            {renderItemContent(index)}
                        </div>
                    ))}
                </div>
            ) : (
                emptyPlaceholder
            )

        return <ListBlock {...restProps} value={data} children={listContent} />
    })
